import * as Accordion from "@radix-ui/react-accordion";
import {
  ChevronDownIcon,
  Cross2Icon,
  ExitIcon,
  HamburgerMenuIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  Pencil1Icon,
} from "@radix-ui/react-icons";
import {
  Avatar,
  Box,
  DropdownMenu,
  Flex,
  IconButton,
  ScrollArea,
  Separator,
  Text,
  TextField,
  Tooltip,
} from "@radix-ui/themes";
import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../hooks/hooks";
import { useIES } from "../hooks/useIES";
import { resetPreviousPath } from "../redux/slices/previousPath";
import { resetUpdateCourseData } from "../redux/slices/school/course";
import { resetUpdateCoursePlanData } from "../redux/slices/school/coursePlan";
import { resetUpdateCourseSubjectData } from "../redux/slices/school/courseSubject";
import { resetStudentData } from "../redux/slices/school/student";
import { resetTeacherData } from "../redux/slices/school/teacher";
import { resetEntitiesData } from "../redux/slices/scientific/entities";
import { resetRoleData } from "../redux/slices/users/role";
import { resetUserData, setUserData } from "../redux/slices/users/user";
import { RootState } from "../redux/store";
import { routes } from "../routes";
import { getUserById } from "../services/user/getUsers";
import { APP_VERSION, AUTH_LOCALSTORAGE } from "../utils/constants";
import { menuItems } from "./menuItems";
import styles from "./Sidebar.module.scss";

const DESKTOP_QUERY = "(min-width: 768px)";

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { IES } = useIES();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia(DESKTOP_QUERY).matches
  );

  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_QUERY);
    const handleChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, []);

  const { Fullname, Role, Permission, UserId } = useSelector(
    (state: RootState) => state.login.userData
  );

  const handleChangePassword = () => {
    navigate(routes.changePassword);
  };

  const handleEditMyUser = async () => {
    try {
      const user = await getUserById(UserId);
      dispatch(setUserData(user));
      navigate(routes.editMyUser);
    } catch {
      toast.error("Erro ao buscar os seus dados.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(AUTH_LOCALSTORAGE);
    window.location.reload();
  };

  const hasAnyPermission = (permissions: string[]) =>
    permissions.some((permission) => Permission && Permission.includes(permission));

  const cleanReduxData = (route: string) => {
    try {
      if (location.pathname === route) return;
      dispatch(resetUserData());
      dispatch(resetTeacherData());
      dispatch(resetUpdateCourseData());
      dispatch(resetUpdateCourseSubjectData());
      dispatch(resetStudentData());
      dispatch(resetRoleData());
      dispatch(resetPreviousPath());
      dispatch(resetEntitiesData());
      dispatch(resetUpdateCoursePlanData());
    } catch (error) {
      console.log(error);
    }
  };

  const routeName = (path: string) => routes.routeName(path);
  const currentRoute = routeName(location.pathname);

  const isSubItemActive = (subItemRoute: string) =>
    currentRoute === routeName(subItemRoute);

  const isParentActive = (item: typeof menuItems[number]) =>
    item.subItems?.some((s) => isSubItemActive(s.route)) ?? false;

  const [openSubMenu, setOpenSubMenu] = useState<string>(() => {
    const match = menuItems.find((item) =>
      item.subItems?.some((s) => currentRoute === routeName(s.route))
    );
    return match?.key ?? "";
  });

  const normalizedSearch = searchTerm.trim().toLowerCase();

  /**
   * Ao pesquisar, filtramos também os sub-itens para que apenas as entradas
   * correspondentes fiquem visíveis dentro de cada grupo.
   */
  const filteredMenuItems = useMemo(() => {
    if (!normalizedSearch) return menuItems;

    return menuItems
      .map((item) => {
        const titleMatches = item.title.toLowerCase().includes(normalizedSearch);
        const matchingSubItems = item.subItems?.filter((subItem) =>
          subItem.title.toLowerCase().includes(normalizedSearch)
        );

        if (titleMatches) return item;
        if (matchingSubItems?.length) return { ...item, subItems: matchingSubItems };
        return null;
      })
      .filter(Boolean) as typeof menuItems;
  }, [normalizedSearch]);

  const visibleMenuItems = filteredMenuItems.filter((item) =>
    hasAnyPermission(item.permissions)
  );

  // Durante a pesquisa, abrimos os grupos correspondentes para revelar os resultados.
  const accordionValue = normalizedSearch
    ? visibleMenuItems.map((item) => item.key)
    : openSubMenu
    ? [openSubMenu]
    : [];

  const roleLabel = Array.isArray(Role) ? Role.join(", ") : Role;

  return (
    <>
      {!isDesktop && (
        <Flex className={styles.topBar}>
          <IconButton
            variant="ghost"
            size="2"
            aria-label={isOpen ? "Fechar menu" : "Abrir menu"}
            aria-expanded={isOpen}
            onClick={() => setIsOpen((p) => !p)}
          >
            {isOpen ? (
              <Cross2Icon width="18" height="18" />
            ) : (
              <HamburgerMenuIcon width="18" height="18" />
            )}
          </IconButton>
          <Text size="2" weight="medium" truncate>
            {IES.NAME !== "N/A" ? IES.NAME : "e-Sura"}
          </Text>
        </Flex>
      )}

      {!isDesktop && isOpen && (
        <Box className={styles.overlay} onClick={() => setIsOpen(false)} />
      )}

      <nav
        aria-label="Menu principal"
        className={[
          styles.sidebar,
          !isDesktop ? styles.sidebarMobile : "",
          !isDesktop && isOpen ? styles.sidebarMobileOpen : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {isDesktop && (
          <Flex align="center" justify="center" pt="4" px="4" style={{ flexShrink: 0 }}>
            <Avatar size="5" fallback="E" radius="none" src={`${IES.LOGO}`} />
          </Flex>
        )}

        <Box px="3" pt="3" pb="2" style={{ flexShrink: 0 }}>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <button type="button" className={styles.userTrigger} aria-label="Menu do utilizador">
                <Flex gap="2" align="center" p="2">
                  <Avatar
                    size="2"
                    radius="full"
                    fallback={Fullname?.charAt(0)?.toUpperCase() ?? "U"}
                  />
                  <Box style={{ minWidth: 0, flex: 1, textAlign: "left" }}>
                    <Text as="div" size="1" weight="bold" truncate>
                      {Fullname}
                    </Text>
                    <Text as="div" size="1" color="gray" truncate>
                      {roleLabel}
                    </Text>
                  </Box>
                  <DropdownMenu.TriggerIcon />
                </Flex>
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content style={{ width: "var(--radix-dropdown-menu-trigger-width)" }}>
              <DropdownMenu.Label>{Fullname}</DropdownMenu.Label>
              <DropdownMenu.Separator />
              <DropdownMenu.Item onClick={handleEditMyUser}>
                <Pencil1Icon /> Editar perfil
              </DropdownMenu.Item>
              <DropdownMenu.Item onClick={handleChangePassword}>
                <LockClosedIcon /> Alterar senha
              </DropdownMenu.Item>
              <DropdownMenu.Separator />
              <DropdownMenu.Item color="red" onClick={handleLogout}>
                <ExitIcon /> Sair
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </Box>

        <Box px="3" pb="2" style={{ flexShrink: 0 }}>
          <TextField.Root
            size="1"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Pesquisar menu…"
            aria-label="Pesquisar no menu"
          >
            <TextField.Slot>
              <MagnifyingGlassIcon height="14" width="14" />
            </TextField.Slot>
            {searchTerm && (
              <TextField.Slot>
                <IconButton
                  type="button"
                  size="1"
                  variant="ghost"
                  color="gray"
                  aria-label="Limpar pesquisa"
                  onClick={() => setSearchTerm("")}
                >
                  <Cross2Icon height="12" width="12" />
                </IconButton>
              </TextField.Slot>
            )}
          </TextField.Root>
        </Box>

        <Separator size="4" />

        <ScrollArea type="hover" scrollbars="vertical" className={styles.scrollArea}>
          <Box px="3" py="2">
            {visibleMenuItems.length === 0 ? (
              <Box className={styles.emptyMenu}>
                <Text size="1" color="gray">
                  Nenhuma entrada corresponde à pesquisa.
                </Text>
              </Box>
            ) : (
              <Accordion.Root
                type="multiple"
                value={accordionValue}
                onValueChange={(values) => {
                  if (normalizedSearch) return;
                  // Mantém o comportamento de um só grupo aberto de cada vez.
                  const next = values.find((v) => v !== openSubMenu);
                  setOpenSubMenu(next ?? "");
                }}
              >
                {visibleMenuItems.map((item) => (
                  <Accordion.Item
                    key={item.key}
                    value={item.key}
                    style={{ marginBottom: "var(--space-1)" }}
                  >
                      <Accordion.Header className={styles.groupHeader}>
                        <Accordion.Trigger
                          className={[
                            styles.groupTrigger,
                            isParentActive(item) ? styles.groupTriggerActive : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          <Flex align="center" gap="2" className={styles.groupLabel}>
                            {item.icon && (
                              <Box className={styles.groupIcon}>
                                <item.icon width="16" height="16" />
                              </Box>
                            )}
                            <Text size="1" truncate>
                              {item.title}
                            </Text>
                          </Flex>
                          <ChevronDownIcon width="14" height="14" className={styles.chevron} />
                        </Accordion.Trigger>
                      </Accordion.Header>

                      <Accordion.Content className={styles.groupContent}>
                        <Flex direction="column" gap="1" pl="4" pt="1">
                          {item.subItems?.map(
                            (subItem) =>
                              Permission?.includes(subItem.permission) && (
                                <Link
                                  key={subItem.route}
                                  to={subItem.route}
                                  aria-current={
                                    isSubItemActive(subItem.route) ? "page" : undefined
                                  }
                                  onClick={() => {
                                    if (!isDesktop) setIsOpen(false);
                                    cleanReduxData(subItem.route);
                                  }}
                                  className={[
                                    styles.link,
                                    isSubItemActive(subItem.route) ? styles.linkActive : "",
                                  ]
                                    .filter(Boolean)
                                    .join(" ")}
                                >
                                  <Text size="1" truncate>
                                    {subItem.title}
                                  </Text>
                                </Link>
                              )
                          )}
                        </Flex>
                      </Accordion.Content>
                  </Accordion.Item>
                ))}
              </Accordion.Root>
            )}
          </Box>
        </ScrollArea>

        <Box
          p="3"
          style={{ borderTop: "1px solid var(--gray-4)", flexShrink: 0, textAlign: "center" }}
        >
          <Tooltip content={`Versão ${APP_VERSION}`}>
            <Text size="1" color="gray">
              e-Sura · v{APP_VERSION}
            </Text>
          </Tooltip>
        </Box>
      </nav>
    </>
  );
};

export default Sidebar;
